<template>
  <v-container fluid class="pl-4 pr-4 pb-4 pt-1">
    <no-projects-alert v-if="noProjects"></no-projects-alert>
    <!-- Header with project chips and controls -->
    <v-row align="center" class="mb-0 px-0" style="min-height: 48px;">
      <v-col cols="auto" class="d-flex align-center ga-2 flex-wrap">
        <!-- Project chips -->
        <v-chip
          v-for="project in projects"
          :key="project.id"
          :color="selectedProjectId === project.id ? 'primary' : 'default'"
          :variant="selectedProjectId === project.id ? 'elevated' : 'outlined'"
          @click="selectProject(project.id)"
          class="cursor-pointer"
        >
          {{ project.name }}
        </v-chip>
      </v-col>
      <v-spacer />
      <v-col cols="auto" class="d-flex align-center ga-2">
        <HelpHint section="statistics" text="Что показывают графики и как их настроить" />
        <v-btn
          icon="mdi-cog"
          variant="outlined"
          @click="settingsModalVisible = true"
          title="Настройки дашборда"
        />
        <v-btn
          :icon="showFilters ? 'mdi-filter-off' : 'mdi-filter'"
          variant="outlined"
          @click="showFilters = !showFilters"
          :color="showFilters ? 'primary' : 'default'"
        />
      </v-col>
    </v-row>

    <v-row>
      <!-- Main content column -->
      <v-col :cols="showFilters ? 10 : 12" class="table-column-transition">
        <!-- Entity Detail View -->
        <template v-if="selectedEntity">
          <v-card class="mb-4">
            <v-card-title class="d-flex align-center">
              <v-btn
                icon="mdi-arrow-left"
                variant="text"
                @click="closeEntityDetail"
                class="mr-2"
              />
              <span class="text-h6">{{ selectedEntity.name }}</span>
              <v-chip class="ml-2" size="small" color="primary" variant="outlined">
                {{ entityTypeLabel }}
              </v-chip>
            </v-card-title>
          </v-card>

          <!-- Stats cards -->
          <v-row class="mb-4">
            <v-col cols="4">
              <v-card
                class="text-center pa-4 clickable-card"
                @click="onMentionsClick"
                title="Открыть сообщения с упоминаниями"
              >
                <div class="text-h4 text-primary">{{ entityStats?.total_mentions || 0 }}</div>
                <div class="text-caption text-grey">Упоминаний</div>
              </v-card>
            </v-col>
            <v-col cols="4">
              <v-card class="text-center pa-4">
                <div class="text-h4">{{ entityStats?.unique_sources || 0 }}</div>
                <div class="text-caption text-grey">Источников</div>
              </v-card>
            </v-col>
            <v-col cols="4">
              <v-card class="text-center pa-4">
                <div class="text-h4 d-flex align-center justify-center">
                  <v-icon :color="trendColor" class="mr-1">{{ trendIcon }}</v-icon>
                  {{ Math.abs(entityStats?.trend_percent || 0) }}%
                </div>
                <div class="text-caption text-grey">Тренд</div>
              </v-card>
            </v-col>
          </v-row>

          <!-- Timeline chart -->
          <v-card>
            <v-card-title class="text-subtitle-1 d-flex align-center">
              <v-icon start>mdi-chart-bar</v-icon>
              Динамика упоминаний
              <v-spacer />
              <v-select
                v-model="entityTimeInterval"
                :items="intervalOptions"
                item-title="text"
                item-value="value"
                density="compact"
                variant="outlined"
                hide-details
                style="max-width: 140px;"
                @update:model-value="onEntityIntervalChange"
              />
            </v-card-title>
            <v-card-text class="chart-container" style="height: 350px;">
              <div v-if="entityStatsLoading" class="d-flex align-center justify-center" style="height: 100%;">
                <v-progress-circular indeterminate color="primary" />
              </div>
              <v-chart
                v-else-if="entityTimelineOptions"
                :option="entityTimelineOptions"
                autoresize
                class="chart clickable-chart"
                @click="onEntityTimelineClick"
              />
              <div v-else class="no-data d-flex align-center justify-center" style="height: 100%;">
                Нет данных для отображения
              </div>
            </v-card-text>
          </v-card>
        </template>

        <!-- Loading state -->
        <div v-else-if="loading" class="text-center pa-8">
          <v-progress-circular indeterminate color="primary" size="64" />
          <div class="mt-4 text-grey">Загрузка статистики...</div>
        </div>

        <!-- Charts grid - dynamically rendered -->
        <draggable
          v-else
          v-model="orderedVisibleWidgets"
          item-key="id"
          class="v-row"
          ghost-class="drag-ghost"
          handle=".drag-handle"
        >
          <template #item="{ element }">
            <v-col cols="12" md="6" :key="element.id">
              <v-card class="chart-card">
                <v-card-title class="text-subtitle-1 d-flex align-center">
                  <v-icon class="drag-handle cursor-move mr-2" size="small">mdi-drag</v-icon>
                  <v-menu v-if="getChartTypeOptions(element.id).length > 1" location="bottom start">
                    <template v-slot:activator="{ props }">
                      <v-btn
                        v-bind="props"
                        :icon="getCurrentChartTypeIcon(element.id)"
                        variant="text"
                        size="small"
                        density="compact"
                        class="mr-1"
                        :title="'Тип диаграммы'"
                      />
                    </template>
                    <v-list density="compact" min-width="140">
                      <v-list-item
                        v-for="ct in getChartTypeOptions(element.id)"
                        :key="ct.type"
                        :active="widgetChartTypes[element.id] === ct.type"
                        @click="setChartType(element.id, ct.type)"
                      >
                        <template v-slot:prepend>
                          <v-icon>{{ ct.icon }}</v-icon>
                        </template>
                        <v-list-item-title>{{ ct.tooltip }}</v-list-item-title>
                      </v-list-item>
                    </v-list>
                  </v-menu>
                  <v-icon v-else start>{{ element.icon }}</v-icon>
                  {{ element.title }}
                  <v-spacer />
                  <!-- Interval selector for messages_over_time -->
                  <v-select
                    v-if="element.id === 'messages_over_time'"
                    v-model="timeInterval"
                    :items="intervalOptions"
                    item-title="text"
                    item-value="value"
                    density="compact"
                    variant="outlined"
                    hide-details
                    style="max-width: 140px;"
                    @update:model-value="loadStats"
                  />
                  <!-- Entity type selector for top_entities -->
                  <v-select
                    v-if="element.id === 'top_entities'"
                    v-model="selectedEntityType"
                    :items="entityTypeOptions"
                    item-title="text"
                    item-value="value"
                    density="compact"
                    variant="outlined"
                    hide-details
                    style="max-width: 160px;"
                  />
                  <AnalysisHelpDialog
                    v-if="element.id === 'manipulations_by_source'"
                    type="manipulations"
                  />
                  <AnalysisHelpDialog
                    v-if="element.id === 'cognitive_biases_by_source'"
                    type="cognitive_biases"
                  />
                  <v-btn
                    icon="mdi-fullscreen"
                    variant="text"
                    size="small"
                    density="compact"
                    class="ml-2"
                    title="На весь экран"
                    @click.stop="openFullscreen(element)"
                  />
                </v-card-title>
                <!-- Source management bar -->
                <div v-if="isSourceWidget(element.id) && getWidgetSources(element.id).length > 0" class="source-bar px-4 pb-1 d-flex align-center flex-wrap ga-1">
                  <v-chip
                    v-for="src in getVisibleSources(element.id)"
                    :key="src.source_id"
                    size="x-small"
                    :color="src.color"
                    variant="flat"
                    class="source-chip"
                    closable
                    @click="openSourceMessages(src.source_id)"
                    @click:close.stop="hideSource(element.id, src.source_id)"
                    :title="src.source_name"
                  >
                    {{ shortName(src.source_name) }}
                  </v-chip>
                  <!-- Restore hidden sources -->
                  <v-chip
                    v-for="src in getHiddenSources(element.id)"
                    :key="'hidden-' + src.source_id"
                    size="x-small"
                    variant="outlined"
                    class="source-chip hidden-source-chip"
                    @click="showSource(element.id, src.source_id)"
                    :title="'Вернуть ' + src.source_name"
                  >
                    <v-icon start size="x-small">mdi-eye-off</v-icon>
                    {{ shortName(src.source_name) }}
                  </v-chip>
                  <!-- Add new source -->
                  <v-menu v-model="addSourceMenuOpen[element.id]" location="bottom start" :close-on-content-click="false">
                    <template v-slot:activator="{ props }">
                      <v-btn
                        v-bind="props"
                        icon="mdi-plus-circle-outline"
                        variant="text"
                        size="x-small"
                        density="compact"
                        color="primary"
                        title="Добавить источник"
                      />
                    </template>
                    <v-card width="500">
                      <v-card-text class="pa-2">
                        <v-autocomplete
                          :items="addSourceSearchResults"
                          item-title="name"
                          item-value="id"
                          return-object
                          density="compact"
                          variant="outlined"
                          placeholder="Поиск источника..."
                          hide-details
                          :loading="allProjectSourcesLoading"
                          no-filter
                          @update:search="onSourceSearch"
                          @update:model-value="(val) => addSourceToWidget(element.id, val)"
                        />
                      </v-card-text>
                    </v-card>
                  </v-menu>
                </div>
                <v-card-text class="chart-container" :class="{ 'pa-0': element.id === 'spread_graph' }">
                  <ChannelGraph
                    v-if="element.id === 'spread_graph' && spreadSourceIds.length"
                    :source-ids="spreadSourceIds"
                    :node-values="spreadNodeValues"
                    hide-side-panel
                    height="350px"
                  />
                  <div v-else-if="element.id === 'spread_graph'" class="no-data d-flex align-center justify-center" style="height: 100%;">
                    Нет данных для отображения
                  </div>
                  <v-chart
                    v-else-if="getChartOptions(element.id)"
                    :option="getChartOptions(element.id)"
                    autoresize
                    :class="['chart', element.clickable ? 'clickable-chart' : '']"
                    @click="element.clickHandler ? element.clickHandler($event) : null"
                  />
                  <div v-else class="no-data">Нет данных</div>
                </v-card-text>
              </v-card>
            </v-col>
          </template>
        </draggable>
      </v-col>

      <!-- Filters panel -->
      <v-slide-x-reverse-transition>
        <v-col v-if="showFilters" cols="2" key="filters">
          <v-card>
            <v-card-title class="text-subtitle-1">Фильтры</v-card-title>
            <v-card-text class="pa-3">
              <v-text-field
                v-model="publishedFrom"
                label="Дата публикации с"
                type="date"
                variant="outlined"
                density="compact"
                clearable
                class="mb-3"
                @update:model-value="loadStats"
              />
              <v-text-field
                v-model="publishedTo"
                label="Дата публикации по"
                type="date"
                variant="outlined"
                density="compact"
                clearable
                @update:model-value="loadStats"
              />
            </v-card-text>
          </v-card>
        </v-col>
      </v-slide-x-reverse-transition>
    </v-row>

    <!-- Fullscreen Widget Modal -->
    <v-dialog v-model="fullscreenVisible" fullscreen transition="dialog-bottom-transition">
      <v-card v-if="fullscreenWidget" class="d-flex flex-column">
        <v-card-title class="text-subtitle-1 d-flex align-center flex-shrink-0">
          <v-icon start>{{ fullscreenWidget.icon }}</v-icon>
          {{ fullscreenWidget.title }}
          <v-spacer />
          <!-- Interval selector for messages_over_time (fullscreen) -->
          <v-select
            v-if="fullscreenWidget.id === 'messages_over_time'"
            v-model="timeInterval"
            :items="intervalOptions"
            item-title="text"
            item-value="value"
            density="compact"
            variant="outlined"
            hide-details
            style="max-width: 140px;"
            @update:model-value="loadStats"
          />
          <!-- Entity type selector for top_entities (fullscreen) -->
          <v-select
            v-if="fullscreenWidget.id === 'top_entities'"
            v-model="selectedEntityType"
            :items="entityTypeOptions"
            item-title="text"
            item-value="value"
            density="compact"
            variant="outlined"
            hide-details
            style="max-width: 160px;"
          />
          <v-btn
            icon="mdi-fullscreen-exit"
            variant="text"
            class="ml-2"
            title="Закрыть"
            @click="fullscreenVisible = false"
          />
        </v-card-title>
        <v-card-text class="flex-grow-1 pa-4" style="min-height: 0;">
          <v-chart
            v-if="getChartOptions(fullscreenWidget.id)"
            :option="getChartOptions(fullscreenWidget.id)"
            autoresize
            style="width: 100%; height: 100%;"
            :class="fullscreenWidget.clickable ? 'clickable-chart' : ''"
            @click="fullscreenWidget.clickHandler ? fullscreenWidget.clickHandler($event) : null"
          />
          <div v-else class="no-data d-flex align-center justify-center" style="height: 100%;">
            Нет данных
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- Settings Modal -->
    <v-dialog v-model="settingsModalVisible" max-width="500px">
      <v-card>
        <v-card-title class="d-flex justify-space-between align-center">
          <span>Настройки дашборда</span>
          <v-btn icon="mdi-close" variant="text" @click="settingsModalVisible = false" />
        </v-card-title>
        <v-divider />
        <v-card-text class="pa-4">
          <div class="text-caption text-grey mb-2">
            Перетащите виджеты для изменения порядка
          </div>
          <draggable
            v-model="widgetSettings"
            item-key="id"
            handle=".settings-drag-handle"
            ghost-class="drag-ghost"
            @end="saveWidgetSettings"
          >
            <template #item="{ element }">
              <div class="widget-setting-item d-flex align-center pa-2 mb-1 rounded" :class="{ 'bg-grey-lighten-4': element.visible }">
                <v-icon class="settings-drag-handle cursor-move mr-3" size="small">mdi-drag</v-icon>
                <v-checkbox
                  v-model="element.visible"
                  :label="element.title"
                  hide-details
                  density="compact"
                  :disabled="isLastVisibleWidget(element)"
                  @update:model-value="onVisibilityChange(element)"
                />
              </div>
            </template>
          </draggable>
          <div v-if="visibleWidgetCount === 1" class="text-caption text-warning mt-2">
            Минимум один виджет должен быть выбран
          </div>
        </v-card-text>
        <v-divider />
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" @click="resetWidgetSettings">Сбросить</v-btn>
          <v-btn color="primary" variant="flat" @click="settingsModalVisible = false">Готово</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import NoProjectsAlert from "@/components/projects/NoProjectsAlert.vue";
import { use } from 'echarts/core';
import { CanvasRenderer } from 'echarts/renderers';
import { PieChart, LineChart, BarChart } from 'echarts/charts';
import {
  TitleComponent,
  TooltipComponent,
  LegendComponent,
  GridComponent
} from 'echarts/components';
import VChart from 'vue-echarts';
import draggable from 'vuedraggable';
import dataClient from '@/apiDataClient';
import apiClient from '@/apiClient';
import { truncate } from '@/utils/text';
import AnalysisHelpDialog, { MANIPULATIONS, COGNITIVE_BIASES } from '@/components/AnalysisHelpDialog.vue';
import ChannelGraph from '@/components/ChannelGraph.vue';

// Register ECharts components
use([
  CanvasRenderer,
  PieChart,
  LineChart,
  BarChart,
  TitleComponent,
  TooltipComponent,
  LegendComponent,
  GridComponent
]);

// Color palette
const COLORS = [
  '#5470c6', '#91cc75', '#fac858', '#ee6666', '#73c0de',
  '#3ba272', '#fc8452', '#9a60b4', '#ea7ccc', '#48b8d0'
];
const OTHER_COLOR = '#c4c4c4';

// Default widget configuration
// Widget IDs that have source-based data (for source management UI)
const SOURCE_WIDGETS = [
  'messages_by_source', 'messages_over_time', 'sentiment_by_source',
  'manipulations_by_source', 'cognitive_biases_by_source'
];

const DEFAULT_WIDGETS = [
  { id: 'messages_by_source', title: 'Сообщения по источникам', icon: 'mdi-chart-pie', visible: true, clickable: true },
  { id: 'messages_over_time', title: 'Динамика по источникам', icon: 'mdi-chart-line', visible: true, clickable: false },
  { id: 'messages_by_platform', title: 'По типу источников', icon: 'mdi-chart-donut', visible: true, clickable: false },
  { id: 'sentiment_by_source', title: 'Тональность по источникам', icon: 'mdi-chart-bar-stacked', visible: true, clickable: true },
  { id: 'top_entities', title: 'Топ сущностей', icon: 'mdi-account-group', visible: true, clickable: true },
  { id: 'manipulations_by_source', title: 'Манипуляции по источникам', icon: 'mdi-alert-decagram', visible: true, clickable: false },
  { id: 'cognitive_biases_by_source', title: 'Когнитивные искажения по источникам', icon: 'mdi-brain', visible: true, clickable: false },
  // Граф связей между источниками с находками проекта (репосты / ссылки / одинаковые посты) —
  // кто у кого берёт тему. Узлы = top-источники из messages_by_source, размер = находок.
  { id: 'spread_graph', title: 'Распространение: связи источников', icon: 'mdi-graph-outline', visible: true, clickable: false },
  // TODO: Тональность по времени — требует разбивки по темам (topics), иначе бессмысленна.
  // Подход 1: Inter-service call — агрегировать по source_id в ES, затем через Core маппить sources → topics.
  // Подход 2: Денормализация — добавить topic_ids в project_findings при создании finding (правильнее, но требует изменения пайплайна).
  // { id: 'sentiment_over_time', title: 'Тональность по времени', icon: 'mdi-chart-timeline-variant', visible: true, clickable: false }
];

// Available chart types per widget
const WIDGET_CHART_TYPES = {
  messages_by_source: [
    { type: 'pie', icon: 'mdi-chart-pie', tooltip: 'Круговая' },
    { type: 'bar', icon: 'mdi-chart-bar', tooltip: 'Столбчатая' }
  ],
  messages_over_time: [
    { type: 'line', icon: 'mdi-chart-line', tooltip: 'Линейная' },
    { type: 'bar', icon: 'mdi-chart-bar', tooltip: 'Столбчатая' },
    { type: 'area', icon: 'mdi-chart-areaspline', tooltip: 'Область' }
  ],
  messages_by_platform: [
    { type: 'pie', icon: 'mdi-chart-pie', tooltip: 'Круговая' },
    { type: 'bar', icon: 'mdi-chart-bar', tooltip: 'Столбчатая' }
  ],
  sentiment_by_source: [
    { type: 'stacked', icon: 'mdi-chart-bar-stacked', tooltip: 'Стопка' },
    { type: 'grouped', icon: 'mdi-chart-bar', tooltip: 'Группировка' }
  ],
  top_entities: [
    { type: 'bar', icon: 'mdi-chart-bar', tooltip: 'Столбчатая' },
    { type: 'pie', icon: 'mdi-chart-pie', tooltip: 'Круговая' }
  ],
  manipulations_by_source: [
    { type: 'stacked', icon: 'mdi-chart-bar-stacked', tooltip: 'Стопка' },
    { type: 'bar', icon: 'mdi-chart-bar', tooltip: 'Столбчатая' }
  ],
  cognitive_biases_by_source: [
    { type: 'stacked', icon: 'mdi-chart-bar-stacked', tooltip: 'Стопка' },
    { type: 'bar', icon: 'mdi-chart-bar', tooltip: 'Столбчатая' }
  ],
  // sentiment_over_time: [
  //   { type: 'line', icon: 'mdi-chart-line', tooltip: 'Линейная' },
  //   { type: 'area', icon: 'mdi-chart-areaspline', tooltip: 'Область' },
  //   { type: 'bar', icon: 'mdi-chart-bar', tooltip: 'Столбчатая' }
  // ]
};

// Default chart type per widget
const DEFAULT_CHART_TYPES = {
  messages_by_source: 'pie',
  messages_over_time: 'line',
  messages_by_platform: 'pie',
  sentiment_by_source: 'stacked',
  top_entities: 'bar',
  manipulations_by_source: 'stacked',
  cognitive_biases_by_source: 'stacked',
  // sentiment_over_time: 'line'
};

// Entity type options
const ENTITY_TYPE_OPTIONS = [
  { text: 'Персоны', value: 'persons' },
  { text: 'Организации', value: 'organizations' },
  { text: 'Локации', value: 'locations' }
];

// Helper function to get cookie (called before component mount)
function getCookieValue(name) {
  const match = document.cookie.match(new RegExp(`${name}=([^;]*)`));
  return match ? match[1] : null;
}

// Load chart types from cookie
function loadChartTypesFromCookie() {
  try {
    const saved = getCookieValue('dashboard_chart_types');
    if (saved) {
      const parsed = JSON.parse(decodeURIComponent(saved));
      return { ...DEFAULT_CHART_TYPES, ...parsed };
    }
  } catch (e) {
    console.error('Error loading chart types:', e);
  }
  return { ...DEFAULT_CHART_TYPES };
}

function loadSourceSettingsFromCookie(key) {
  try {
    const saved = getCookieValue(key);
    if (saved) return JSON.parse(decodeURIComponent(saved));
  } catch (e) {
    console.error(`Error loading ${key}:`, e);
  }
  return {};
}

function saveSourceSettingsToCookie(key, value) {
  const encoded = encodeURIComponent(JSON.stringify(value));
  document.cookie = `${key}=${encoded};path=/;max-age=31536000`;
}

// Load widget settings from cookie
function loadWidgetSettingsFromCookie() {
  try {
    const saved = getCookieValue('dashboard_widget_settings');
    if (saved) {
      const parsed = JSON.parse(decodeURIComponent(saved));
      const settingsMap = {};
      parsed.forEach(w => { settingsMap[w.id] = w; });

      return DEFAULT_WIDGETS.map(dw => ({
        ...dw,
        visible: settingsMap[dw.id]?.visible ?? dw.visible,
        order: parsed.findIndex(p => p.id === dw.id)
      })).sort((a, b) => {
        if (a.order === -1) return 1;
        if (b.order === -1) return -1;
        return a.order - b.order;
      }).map(({ order, ...rest }) => rest);
    }
  } catch (e) {
    console.error('Error loading widget settings:', e);
  }
  return JSON.parse(JSON.stringify(DEFAULT_WIDGETS));
}

export default {
  name: 'Dashboard',
  components: {
    NoProjectsAlert,
    VChart,
    draggable,
    AnalysisHelpDialog,
    ChannelGraph
  },
  data() {
    return {
      noProjects: false,
      loading: true,
      projects: [],
      selectedProjectId: null,
      stats: null,
      showFilters: false,
      publishedFrom: null,
      publishedTo: null,
      timeInterval: 'day',
      intervalOptions: [
        { text: 'По дням', value: 'day' },
        { text: 'По неделям', value: 'week' },
        { text: 'По месяцам', value: 'month' }
      ],
      settingsModalVisible: false,
      widgetSettings: loadWidgetSettingsFromCookie(),
      selectedEntityType: 'persons',
      entityTypeOptions: ENTITY_TYPE_OPTIONS,
      // Chart type per widget
      widgetChartTypes: loadChartTypesFromCookie(),
      // Hidden sources per widget: { widgetId: [sourceId1, sourceId2, ...] }
      widgetHiddenSources: loadSourceSettingsFromCookie('dashboard_hidden_sources'),
      // Extra sources added by user (not in top N): { widgetId: [{ source_id, source_name }, ...] }
      widgetExtraSources: loadSourceSettingsFromCookie('dashboard_extra_sources'),
      // Add-source UI state
      addSourceMenuOpen: {},
      addSourceSearchResults: [],
      allProjectSourcesLoading: false,
      sourceSearchTimeout: null,
      // Fullscreen widget state
      fullscreenVisible: false,
      fullscreenWidget: null,
      // Entity Detail View state
      selectedEntity: null,
      entityStats: null,
      entityStatsLoading: false,
      entityTimeInterval: 'day'
    };
  },
  computed: {
    visibleWidgetCount() {
      return this.widgetSettings.filter(w => w.visible).length;
    },

    orderedVisibleWidgets: {
      get() {
        return this.widgetSettings
          .filter(w => w.visible)
          .map(w => ({
            ...w,
            clickHandler: this.getClickHandler(w.id)
          }));
      },
      set(newOrder) {
        // Reorder widgetSettings based on new visible order
        const newOrderIds = newOrder.map(w => w.id);
        const hiddenWidgets = this.widgetSettings.filter(w => !w.visible);
        const reorderedVisible = newOrderIds.map(id =>
          this.widgetSettings.find(w => w.id === id)
        ).filter(Boolean);

        this.widgetSettings = [...reorderedVisible, ...hiddenWidgets];
        this.saveWidgetSettings();
      }
    },

    // «Распространение»: источники с находками (top из messages_by_source) → узлы графа связей
    spreadSourceIds() {
      const items = this.stats?.messages_by_source?.items || [];
      return items.map(i => i.source_id).filter(Boolean);
    },
    spreadNodeValues() {
      const out = {};
      (this.stats?.messages_by_source?.items || []).forEach(i => { if (i.source_id) out[i.source_id] = i.count || 0; });
      return out;
    },
    messagesBySourceOptions() {
      const data = this.stats?.messages_by_source;
      if (!data || !data.items || data.items.length === 0) return null;

      const filteredItems = this.filterSourceItems('messages_by_source', data.items);
      if (filteredItems.length === 0) return null;

      const chartData = filteredItems.map((item, index) => ({
        name: item.source_name,
        value: item.count,
        sourceId: item.source_id,
        itemStyle: { color: COLORS[index % COLORS.length] }
      }));

      if (data.other_count > 0) {
        chartData.push({
          name: 'Другие',
          value: data.other_count,
          sourceId: null,
          itemStyle: { color: OTHER_COLOR }
        });
      }

      const chartType = this.widgetChartTypes.messages_by_source;

      if (chartType === 'bar') {
        const names = chartData.map(d => d.name);
        const values = chartData.map(d => d.value);
        return {
          tooltip: {
            trigger: 'axis',
            axisPointer: { type: 'shadow' },
            formatter: (params) => {
              const p = params[0];
              return `<strong>${p.name}</strong><br/>${p.value}`;
            }
          },
          grid: {
            left: '3%',
            right: '6%',
            bottom: '3%',
            top: '3%',
            containLabel: true
          },
          xAxis: { type: 'value' },
          yAxis: {
            type: 'category',
            data: names,
            axisLabel: { fontSize: 12, width: 190, overflow: 'truncate' }
          },
          series: [{
            type: 'bar',
            data: chartData.map(d => ({
              value: d.value,
              itemStyle: d.itemStyle
            })),
            label: { show: true, position: 'right', formatter: '{c}' },
            barMaxWidth: 30
          }]
        };
      }

      return {
        tooltip: {
          trigger: 'item',
          formatter: '{b}: {c} ({d}%)'
        },
        legend: {
          type: 'scroll', orient: 'vertical', right: 10, top: 'center',
          formatter: (name) => truncate(name),
        },
        series: [{
          type: 'pie',
          radius: ['40%', '70%'],
          center: ['40%', '50%'],
          avoidLabelOverlap: true,
          itemStyle: {
            borderRadius: 4,
            borderColor: '#fff',
            borderWidth: 2
          },
          label: { show: false },
          emphasis: {
            label: { show: true, fontWeight: 'bold' }
          },
          data: chartData
        }]
      };
    },

    messagesOverTimeOptions() {
      const data = this.stats?.messages_over_time;
      if (!data || !data.dates || data.dates.length === 0) return null;

      const chartType = this.widgetChartTypes.messages_over_time;
      const seriesType = chartType === 'area' ? 'line' : chartType;

      const filteredSeries = this.filterSourceItems('messages_over_time', data.series);

      const series = filteredSeries.map((s, index) => ({
        name: s.source_name,
        type: seriesType,
        smooth: seriesType === 'line',
        symbol: seriesType === 'line' ? 'none' : undefined,
        data: s.data,
        ...(chartType === 'area' ? {
          areaStyle: { opacity: 0.3 }
        } : {}),
        lineStyle: { color: COLORS[index % COLORS.length] },
        itemStyle: { color: COLORS[index % COLORS.length] }
      }));

      if (data.other && data.other.some(v => v > 0)) {
        series.push({
          name: 'Другие',
          type: seriesType,
          smooth: seriesType === 'line',
          symbol: seriesType === 'line' ? 'none' : undefined,
          data: data.other,
          ...(chartType === 'area' ? {
            areaStyle: { opacity: 0.2 }
          } : {}),
          lineStyle: { color: OTHER_COLOR, type: 'dashed' },
          itemStyle: { color: OTHER_COLOR }
        });
      }

      const formatDate = (d) => {
        if (data.interval === 'month') {
          return d;
        } else if (data.interval === 'week') {
          const date = new Date(d);
          const weekNum = this.getWeekNumber(date);
          return `Нед ${weekNum}`;
        } else {
          const date = new Date(d);
          return `${date.getDate()}.${date.getMonth() + 1}`;
        }
      };

      return {
        tooltip: { trigger: 'axis' },
        legend: { type: 'scroll', top: 0, formatter: (name) => truncate(name) },
        grid: {
          left: '3%',
          right: '4%',
          bottom: '3%',
          top: 40,
          containLabel: true
        },
        xAxis: {
          type: 'category',
          boundaryGap: chartType === 'bar',
          data: data.dates.map(formatDate)
        },
        yAxis: { type: 'value' },
        series
      };
    },

    messagesByPlatformOptions() {
      const data = this.stats?.messages_by_platform;
      if (!data || !data.items || data.items.length === 0) return null;

      const platformColors = {
        telegram: '#0088cc',
        vk: '#4a76a8',
        youtube: '#ff0000',
        facebook: '#1877f2',
        instagram: '#E1306C',
        twitter: '#1DA1F2',
        web: '#666666',
        rss: '#ff6600'
      };

      const chartData = data.items.map(item => ({
        name: item.platform_name || item.platform,
        value: item.count,
        itemStyle: { color: platformColors[item.platform] || COLORS[0] }
      }));

      const chartType = this.widgetChartTypes.messages_by_platform;

      if (chartType === 'bar') {
        return {
          tooltip: {
            trigger: 'axis',
            axisPointer: { type: 'shadow' },
            formatter: (params) => {
              const p = params[0];
              return `<strong>${p.name}</strong><br/>${p.value}`;
            }
          },
          grid: {
            left: '3%',
            right: '6%',
            bottom: '3%',
            top: '3%',
            containLabel: true
          },
          xAxis: { type: 'value' },
          yAxis: {
            type: 'category',
            data: chartData.map(d => d.name),
            axisLabel: { fontSize: 12, width: 190, overflow: 'truncate' }
          },
          series: [{
            type: 'bar',
            data: chartData.map(d => ({
              value: d.value,
              itemStyle: d.itemStyle
            })),
            label: { show: true, position: 'right', formatter: '{c}' },
            barMaxWidth: 30
          }]
        };
      }

      return {
        tooltip: {
          trigger: 'item',
          formatter: '{b}: {c} ({d}%)'
        },
        legend: {
          orient: 'vertical',
          right: 10,
          top: 'center',
          formatter: (name) => truncate(name),
        },
        series: [{
          type: 'pie',
          radius: ['40%', '70%'],
          center: ['35%', '50%'],
          avoidLabelOverlap: true,
          itemStyle: {
            borderRadius: 4,
            borderColor: '#fff',
            borderWidth: 2
          },
          label: { show: false },
          emphasis: {
            label: { show: true, fontWeight: 'bold' }
          },
          data: chartData
        }]
      };
    },

    sentimentBySourceOptions() {
      const data = this.stats?.sentiment_by_source;
      if (!data || !data.items || data.items.length === 0) return null;

      const filteredItems = this.filterSourceItems('sentiment_by_source', data.items);
      if (filteredItems.length === 0) return null;

      const fullNames = filteredItems.map(item => item.source_name);
      const positiveData = filteredItems.map(item => item.positive);
      const neutralData = filteredItems.map(item => item.neutral);
      const negativeData = filteredItems.map(item => item.negative);
      const chartType = this.widgetChartTypes.sentiment_by_source;
      const stackValue = chartType === 'stacked' ? 'total' : undefined;

      return {
        tooltip: {
          trigger: 'axis',
          axisPointer: { type: 'shadow' },
          formatter: (params) => {
            const idx = params[0].dataIndex;
            const fullName = fullNames[idx];
            let result = `<strong>${fullName}</strong><br/>`;
            params.forEach(p => {
              result += `${p.marker} ${p.seriesName}: ${p.value}<br/>`;
            });
            return result;
          }
        },
        legend: {
          data: ['Позитив', 'Нейтральный', 'Негатив'],
          bottom: 0
        },
        grid: {
          left: '3%',
          right: '2%',
          bottom: '15%',
          top: '3%',
          containLabel: true
        },
        xAxis: { type: 'value' },
        yAxis: {
          type: 'category',
          data: fullNames,
          axisLabel: {
            fontSize: 12,
            width: 190,
            overflow: 'truncate'
          }
        },
        series: [
          {
            name: 'Позитив',
            type: 'bar',
            stack: stackValue,
            emphasis: { focus: 'series' },
            data: positiveData,
            itemStyle: { color: '#4CAF50' }
          },
          {
            name: 'Нейтральный',
            type: 'bar',
            stack: stackValue,
            emphasis: { focus: 'series' },
            data: neutralData,
            itemStyle: { color: '#9E9E9E' }
          },
          {
            name: 'Негатив',
            type: 'bar',
            stack: stackValue,
            emphasis: { focus: 'series' },
            data: negativeData,
            itemStyle: { color: '#F44336' }
          }
        ]
      };
    },

    topEntitiesOptions() {
      const topEntities = this.stats?.top_entities;
      if (!topEntities) return null;

      const data = topEntities[this.selectedEntityType];
      if (!data || !data.items || data.items.length === 0) return null;

      const chartType = this.widgetChartTypes.top_entities;

      if (chartType === 'pie') {
        const pieData = data.items.map((item, index) => ({
          name: item.name,
          value: item.count,
          itemStyle: { color: COLORS[index % COLORS.length] }
        }));
        return {
          tooltip: {
            trigger: 'item',
            formatter: '{b}: {c} ({d}%)'
          },
          legend: {
            type: 'scroll',
            orient: 'vertical',
            right: 10,
            top: 20,
            bottom: 20
          },
          series: [{
            type: 'pie',
            radius: ['40%', '70%'],
            center: ['35%', '50%'],
            avoidLabelOverlap: true,
            itemStyle: {
              borderRadius: 4,
              borderColor: '#fff',
              borderWidth: 2
            },
            label: { show: false },
            emphasis: {
              label: { show: true, fontWeight: 'bold' }
            },
            data: pieData
          }]
        };
      }

      // Reverse for horizontal bar chart (top at the top)
      const items = [...data.items].reverse();
      const names = items.map(item => item.name);
      const counts = items.map(item => item.count);
      const maxCount = Math.max(...counts);

      return {
        tooltip: {
          trigger: 'axis',
          axisPointer: { type: 'shadow' },
          formatter: (params) => {
            const p = params[0];
            return `<strong>${p.name}</strong><br/>Упоминаний: ${p.value}`;
          }
        },
        grid: {
          left: '3%',
          right: '6%',
          bottom: '3%',
          top: '3%',
          containLabel: true
        },
        xAxis: {
          type: 'value',
          max: maxCount
        },
        yAxis: {
          type: 'category',
          data: names,
          axisLabel: {
            fontSize: 12,
            width: 190,
            overflow: 'truncate'
          }
        },
        series: [{
          type: 'bar',
          data: counts.map((value, index) => ({
            value,
            itemStyle: {
              color: COLORS[index % COLORS.length]
            }
          })),
          label: {
            show: true,
            position: 'right',
            formatter: '{c}'
          },
          barMaxWidth: 30
        }]
      };
    },

    manipulationsBySourceOptions() {
      const data = this.stats?.manipulations_by_source;
      if (!data || !data.items || data.items.length === 0) return null;

      const filteredItems = this.filterSourceItems('manipulations_by_source', data.items);
      if (filteredItems.length === 0) return null;

      const chartType = this.widgetChartTypes.manipulations_by_source;

      // Collect types present in data, ordered by MANIPULATIONS reference
      const presentTypes = new Set();
      filteredItems.forEach(item => {
        Object.keys(item.types || {}).forEach(t => presentTypes.add(t));
      });
      // Use reference order from AnalysisHelpDialog
      const manipColorMap = {};
      const manipNameMap = {};
      MANIPULATIONS.forEach(m => { manipColorMap[m.code] = m.color; manipNameMap[m.code] = m.name; });
      const typesList = MANIPULATIONS
        .filter(m => presentTypes.has(m.code))
        .map(m => m.code);

      const names = filteredItems.map(item => item.source_name);
      const stackValue = chartType === 'stacked' ? 'manip' : undefined;

      const series = typesList.map((type, index) => ({
        name: manipNameMap[type] || type,
        type: 'bar',
        stack: stackValue,
        emphasis: { focus: 'series' },
        data: filteredItems.map(item => (item.types || {})[type] || 0),
        itemStyle: { color: manipColorMap[type] || COLORS[index % COLORS.length] }
      }));

      return {
        tooltip: {
          trigger: 'axis',
          axisPointer: { type: 'shadow' },
          appendToBody: true
        },
        legend: { type: 'scroll', bottom: 0 },
        grid: {
          left: '3%',
          right: '2%',
          bottom: '15%',
          top: '3%',
          containLabel: true
        },
        xAxis: { type: 'value' },
        yAxis: {
          type: 'category',
          data: names,
          axisLabel: { fontSize: 12, width: 190, overflow: 'truncate' }
        },
        series
      };
    },

    cognitiveBiasesBySourceOptions() {
      const data = this.stats?.cognitive_biases_by_source;
      if (!data || !data.items || data.items.length === 0) return null;

      const filteredItems = this.filterSourceItems('cognitive_biases_by_source', data.items);
      if (filteredItems.length === 0) return null;

      const chartType = this.widgetChartTypes.cognitive_biases_by_source;

      // Collect types present in data, ordered by COGNITIVE_BIASES reference
      const presentTypes = new Set();
      filteredItems.forEach(item => {
        Object.keys(item.types || {}).forEach(t => presentTypes.add(t));
      });
      const biasColorMap = {};
      const biasNameMap = {};
      COGNITIVE_BIASES.forEach(b => { biasColorMap[b.code] = b.color; biasNameMap[b.code] = b.name; });
      const typesList = COGNITIVE_BIASES
        .filter(b => presentTypes.has(b.code))
        .map(b => b.code);

      const names = filteredItems.map(item => item.source_name);
      const stackValue = chartType === 'stacked' ? 'bias' : undefined;

      const series = typesList.map((type, index) => ({
        name: biasNameMap[type] || type,
        type: 'bar',
        stack: stackValue,
        emphasis: { focus: 'series' },
        data: filteredItems.map(item => (item.types || {})[type] || 0),
        itemStyle: { color: biasColorMap[type] || COLORS[index % COLORS.length] }
      }));

      return {
        tooltip: {
          trigger: 'axis',
          axisPointer: { type: 'shadow' },
          appendToBody: true
        },
        legend: { type: 'scroll', bottom: 0 },
        grid: {
          left: '3%',
          right: '2%',
          bottom: '15%',
          top: '3%',
          containLabel: true
        },
        xAxis: { type: 'value' },
        yAxis: {
          type: 'category',
          data: names,
          axisLabel: { fontSize: 12, width: 190, overflow: 'truncate' }
        },
        series
      };
    },

    // TODO: sentimentOverTimeOptions — отложен, требует разбивки по темам (topics).
    // См. DEFAULT_WIDGETS для описания двух подходов реализации.

    entityTimelineOptions() {
      if (!this.entityStats || !this.entityStats.timeline || this.entityStats.timeline.length === 0) {
        return null;
      }

      const timeline = this.entityStats.timeline;
      const dates = timeline.map(t => {
        if (this.entityTimeInterval === 'month') {
          return t.date.substring(0, 7);
        } else if (this.entityTimeInterval === 'week') {
          const date = new Date(t.date);
          const weekNum = this.getWeekNumber(date);
          return `Нед ${weekNum}`;
        } else {
          const date = new Date(t.date);
          return `${date.getDate()}.${date.getMonth() + 1}`;
        }
      });
      const counts = timeline.map(t => t.count);

      return {
        tooltip: {
          trigger: 'axis',
          formatter: (params) => {
            const p = params[0];
            return `${p.name}<br/>Упоминаний: ${p.value}`;
          }
        },
        grid: {
          left: '3%',
          right: '4%',
          bottom: '10%',
          top: '10%',
          containLabel: true
        },
        xAxis: {
          type: 'category',
          boundaryGap: false,
          data: dates,
          axisLabel: {
            rotate: dates.length > 20 ? 45 : 0
          }
        },
        yAxis: {
          type: 'value',
          minInterval: 1
        },
        series: [{
          type: 'line',
          data: counts,
          smooth: true,
          areaStyle: {
            color: {
              type: 'linear',
              x: 0,
              y: 0,
              x2: 0,
              y2: 1,
              colorStops: [
                { offset: 0, color: 'rgba(84, 112, 198, 0.5)' },
                { offset: 1, color: 'rgba(84, 112, 198, 0.1)' }
              ]
            }
          },
          lineStyle: {
            color: '#5470c6',
            width: 2
          },
          itemStyle: {
            color: '#5470c6'
          },
          symbol: 'circle',
          symbolSize: 6
        }]
      };
    },

    entityTypeLabel() {
      const labels = {
        'PER': 'Персона',
        'ORG': 'Организация',
        'LOC': 'Локация'
      };
      return this.selectedEntity ? labels[this.selectedEntity.entity_type] || this.selectedEntity.entity_type : '';
    },

    trendIcon() {
      if (!this.entityStats) return 'mdi-arrow-right';
      const trend = this.entityStats.trend_percent;
      if (trend > 0) return 'mdi-arrow-up';
      if (trend < 0) return 'mdi-arrow-down';
      return 'mdi-arrow-right';
    },

    trendColor() {
      if (!this.entityStats) return 'grey';
      const trend = this.entityStats.trend_percent;
      if (trend > 0) return 'green';
      if (trend < 0) return 'red';
      return 'grey';
    }
  },
  methods: {
    /** Короткая подпись для чипа; полное название уходит в подсказку. */
    shortName(value) {
      return truncate(value);
    },
    getChartOptions(widgetId) {
      const optionsMap = {
        'messages_by_source': this.messagesBySourceOptions,
        'messages_over_time': this.messagesOverTimeOptions,
        'messages_by_platform': this.messagesByPlatformOptions,
        'sentiment_by_source': this.sentimentBySourceOptions,
        'top_entities': this.topEntitiesOptions,
        'manipulations_by_source': this.manipulationsBySourceOptions,
        'cognitive_biases_by_source': this.cognitiveBiasesBySourceOptions,
        // 'sentiment_over_time': this.sentimentOverTimeOptions
      };
      return optionsMap[widgetId];
    },

    getClickHandler(widgetId) {
      const handlers = {
        'messages_by_source': this.onMessagesBySourceClick,
        'sentiment_by_source': this.onSentimentBySourceClick,
        'top_entities': this.onTopEntitiesClick
      };
      return handlers[widgetId] || null;
    },

    isSourceWidget(widgetId) {
      return SOURCE_WIDGETS.includes(widgetId);
    },

    getWidgetSources(widgetId) {
      // Extract all sources from stats data for a given widget
      const sourceMap = {
        'messages_by_source': () => {
          const d = this.stats?.messages_by_source;
          return (d?.items || []).map((item, i) => ({
            source_id: item.source_id,
            source_name: item.source_name,
            color: item.source_id ? COLORS[i % COLORS.length] : OTHER_COLOR
          }));
        },
        'messages_over_time': () => {
          const d = this.stats?.messages_over_time;
          return (d?.series || []).map((s, i) => ({
            source_id: s.source_id,
            source_name: s.source_name,
            color: COLORS[i % COLORS.length]
          }));
        },
        'sentiment_by_source': () => {
          const d = this.stats?.sentiment_by_source;
          return (d?.items || []).map((item, i) => ({
            source_id: item.source_id,
            source_name: item.source_name,
            color: COLORS[i % COLORS.length]
          }));
        },
        'manipulations_by_source': () => {
          const d = this.stats?.manipulations_by_source;
          return (d?.items || []).map((item, i) => ({
            source_id: item.source_id,
            source_name: item.source_name,
            color: COLORS[i % COLORS.length]
          }));
        },
        'cognitive_biases_by_source': () => {
          const d = this.stats?.cognitive_biases_by_source;
          return (d?.items || []).map((item, i) => ({
            source_id: item.source_id,
            source_name: item.source_name,
            color: COLORS[i % COLORS.length]
          }));
        }
      };
      const fn = sourceMap[widgetId];
      const statsSources = fn ? fn() : [];
      // Append extra sources added by user (not in top-N from ES)
      const extras = this.widgetExtraSources[widgetId] || [];
      const existingIds = new Set(statsSources.map(s => s.source_id));
      const extraMapped = extras
        .filter(e => !existingIds.has(e.source_id))
        .map((e, i) => ({
          source_id: e.source_id,
          source_name: e.source_name,
          color: COLORS[(statsSources.length + i) % COLORS.length]
        }));
      return [...statsSources, ...extraMapped];
    },

    getVisibleSources(widgetId) {
      const hidden = this.widgetHiddenSources[widgetId] || [];
      return this.getWidgetSources(widgetId).filter(s => !hidden.includes(s.source_id));
    },

    getHiddenSources(widgetId) {
      const hidden = this.widgetHiddenSources[widgetId] || [];
      return this.getWidgetSources(widgetId).filter(s => hidden.includes(s.source_id));
    },

    hideSource(widgetId, sourceId) {
      // If this is an extra source (user-added), remove it entirely and reload
      const extras = this.widgetExtraSources[widgetId] || [];
      const extraIdx = extras.findIndex(s => s.source_id === sourceId);
      if (extraIdx !== -1) {
        extras.splice(extraIdx, 1);
        saveSourceSettingsToCookie('dashboard_extra_sources', this.widgetExtraSources);
        this.loadStats();
        return;
      }
      // Otherwise hide from top-N sources (client-side filter)
      if (!this.widgetHiddenSources[widgetId]) {
        this.widgetHiddenSources[widgetId] = [];
      }
      if (!this.widgetHiddenSources[widgetId].includes(sourceId)) {
        this.widgetHiddenSources[widgetId].push(sourceId);
        saveSourceSettingsToCookie('dashboard_hidden_sources', this.widgetHiddenSources);
      }
    },

    showSource(widgetId, sourceId) {
      if (this.widgetHiddenSources[widgetId]) {
        this.widgetHiddenSources[widgetId] = this.widgetHiddenSources[widgetId].filter(id => id !== sourceId);
        saveSourceSettingsToCookie('dashboard_hidden_sources', this.widgetHiddenSources);
      }
    },

    isSourceHidden(widgetId, sourceId) {
      return (this.widgetHiddenSources[widgetId] || []).includes(sourceId);
    },

    filterSourceItems(widgetId, items) {
      const hidden = this.widgetHiddenSources[widgetId] || [];
      if (hidden.length === 0) return items;
      return items.filter(item => !hidden.includes(item.source_id));
    },

    openSourceMessages(sourceId) {
      if (!sourceId || !this.selectedProjectId) return;
      const maxAge = 31536000;
      document.cookie = `messages_sourceId=${sourceId};path=/;max-age=${maxAge}`;
      document.cookie = `messages_selected_project=${this.selectedProjectId};path=/;max-age=${maxAge}`;
      window.open(this.$router.resolve('/monitoring/messages').href, '_blank');
    },

    onSourceSearch(query) {
      if (this.sourceSearchTimeout) {
        clearTimeout(this.sourceSearchTimeout);
      }
      if (!query || query.length < 2) {
        this.addSourceSearchResults = [];
        return;
      }
      this.sourceSearchTimeout = setTimeout(() => {
        this.searchSources(query);
      }, 300);
    },

    async searchSources(query) {
      this.allProjectSourcesLoading = true;
      try {
        const response = await apiClient.get('/v2/platform-sources', {
          params: { display_name: query, limit: 20 }
        });
        const sources = response.data?.items || response.data || [];
        // Get all currently visible source IDs across all widgets to mark them
        this.addSourceSearchResults = sources.map(s => ({
          id: s.id,
          name: s.display_name || s.username || `Source #${s.id}`
        }));
      } catch (error) {
        console.error('Error searching sources:', error);
        this.addSourceSearchResults = [];
      } finally {
        this.allProjectSourcesLoading = false;
      }
    },

    addSourceToWidget(widgetId, sourceObj) {
      if (!sourceObj || !sourceObj.id) return;
      const sourceId = sourceObj.id;
      const sourceName = sourceObj.name;
      // Ensure reactive property exists
      if (!this.widgetExtraSources[widgetId]) {
        this.widgetExtraSources[widgetId] = [];
      }
      // Don't add if already in extra sources
      if (this.widgetExtraSources[widgetId].some(s => s.source_id === sourceId)) return;
      // Also don't add if already visible from stats data
      const existingSources = this.getWidgetSources(widgetId);
      if (existingSources.some(s => s.source_id === sourceId)) {
        // Maybe it's hidden - restore it
        this.showSource(widgetId, sourceId);
      } else {
        this.widgetExtraSources[widgetId].push({
          source_id: sourceId,
          source_name: sourceName
        });
        saveSourceSettingsToCookie('dashboard_extra_sources', this.widgetExtraSources);
        // Reload stats with increased source_limit to include new source
        this.loadStats();
      }
      // Close menu and clear search
      this.addSourceMenuOpen[widgetId] = false;
      this.addSourceSearchResults = [];
    },

    getChartTypeOptions(widgetId) {
      return WIDGET_CHART_TYPES[widgetId] || [];
    },

    getCurrentChartTypeIcon(widgetId) {
      const currentType = this.widgetChartTypes[widgetId];
      const options = WIDGET_CHART_TYPES[widgetId] || [];
      const found = options.find(o => o.type === currentType);
      return found ? found.icon : 'mdi-chart-bar';
    },

    setChartType(widgetId, type) {
      this.widgetChartTypes[widgetId] = type;
      const encoded = encodeURIComponent(JSON.stringify(this.widgetChartTypes));
      document.cookie = `dashboard_chart_types=${encoded};path=/;max-age=31536000`;
    },

    openFullscreen(widget) {
      this.fullscreenWidget = {
        ...widget,
        clickHandler: this.getClickHandler(widget.id)
      };
      this.fullscreenVisible = true;
    },

    isLastVisibleWidget(widget) {
      return widget.visible && this.visibleWidgetCount === 1;
    },

    onVisibilityChange() {
      this.saveWidgetSettings();
    },

    saveWidgetSettings() {
      const settings = this.widgetSettings.map(w => ({
        id: w.id,
        visible: w.visible
      }));
      const encoded = encodeURIComponent(JSON.stringify(settings));
      document.cookie = `dashboard_widget_settings=${encoded};path=/;max-age=31536000`;
    },

    resetWidgetSettings() {
      this.widgetSettings = JSON.parse(JSON.stringify(DEFAULT_WIDGETS));
      this.saveWidgetSettings();
    },

    getWeekNumber(date) {
      const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
      const dayNum = d.getUTCDay() || 7;
      d.setUTCDate(d.getUTCDate() + 4 - dayNum);
      const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
      return Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
    },

    async loadProjects() {
      try {
        const response = await apiClient.get('/available-projects');
        this.projects = response.data || [];
        this.noProjects = !this.projects.length;

        if (this.projects.length > 0 && !this.selectedProjectId) {
          const savedProjectId = this.getSavedProjectId();
          if (savedProjectId && this.projects.some(p => p.id === savedProjectId)) {
            this.selectedProjectId = savedProjectId;
          } else {
            this.selectedProjectId = this.projects[0].id;
          }
        }
      } catch (error) {
        console.error('Error loading projects:', error);
        this.projects = [];
      }
    },

    async loadStats() {
      if (!this.selectedProjectId) {
        this.loading = false;
        this.stats = null;
        return;
      }

      this.loading = true;
      try {
        // Build per-widget extra source IDs map
        const extraSourcesMap = {};
        for (const [widgetId, sources] of Object.entries(this.widgetExtraSources)) {
          if (sources.length > 0) {
            extraSourcesMap[widgetId] = sources.map(s => s.source_id);
          }
        }
        const params = {
          project_id: this.selectedProjectId,
          interval: this.timeInterval
        };
        if (Object.keys(extraSourcesMap).length > 0) {
          params.extra_sources = JSON.stringify(extraSourcesMap);
        }

        if (this.publishedFrom) {
          params.published_from = this.publishedFrom + 'T00:00:00';
        }
        if (this.publishedTo) {
          params.published_to = this.publishedTo + 'T23:59:59';
        }

        const response = await dataClient.get('/dashboard/stats', { params });
        this.stats = response.data;
      } catch (error) {
        console.error('Error loading dashboard stats:', error);
        this.stats = null;
      } finally {
        this.loading = false;
      }
    },

    selectProject(projectId) {
      if (this.selectedProjectId === projectId) return;
      this.selectedProjectId = projectId;
      document.cookie = `dashboard_selected_project=${projectId};path=/;max-age=31536000`;
      this.loadStats();
    },

    getSavedProjectId() {
      const match = document.cookie.match(/dashboard_selected_project=(\d+)/);
      return match ? parseInt(match[1], 10) : null;
    },

    onMessagesBySourceClick(params) {
      const sourceId = params.data?.sourceId;
      if (!sourceId) return;

      const maxAge = 31536000;
      document.cookie = `messages_sourceId=${sourceId};path=/;max-age=${maxAge}`;

      if (this.selectedProjectId) {
        document.cookie = `messages_selected_project=${this.selectedProjectId};path=/;max-age=${maxAge}`;
      }

      this.$router.push('/monitoring/messages');
    },

    onSentimentBySourceClick(params) {
      const dataIndex = params.dataIndex;
      const sentimentData = this.stats?.sentiment_by_source?.items;

      if (dataIndex === undefined || !sentimentData || !sentimentData[dataIndex]) {
        return;
      }

      const sourceId = sentimentData[dataIndex].source_id;
      if (!sourceId) return;

      const maxAge = 31536000;
      document.cookie = `messages_sourceId=${sourceId};path=/;max-age=${maxAge}`;

      if (this.selectedProjectId) {
        document.cookie = `messages_selected_project=${this.selectedProjectId};path=/;max-age=${maxAge}`;
      }

      this.$router.push('/monitoring/messages');
    },

    onTopEntitiesClick(params) {
      // Get entity data from chart click
      const dataIndex = params.dataIndex;
      const topEntities = this.stats?.top_entities;
      if (!topEntities) return;

      const data = topEntities[this.selectedEntityType];
      if (!data || !data.items) return;

      // Items are reversed in chart, so we need to reverse index
      const reversedItems = [...data.items].reverse();
      const entity = reversedItems[dataIndex];
      if (!entity) return;

      // Map selectedEntityType to entity_type
      const typeMapping = {
        'persons': 'PER',
        'organizations': 'ORG',
        'locations': 'LOC'
      };

      this.selectedEntity = {
        id: entity.id,
        name: entity.name,
        canonical_name: entity.canonical_name || entity.name,
        entity_type: typeMapping[this.selectedEntityType],
        aliases: entity.aliases || []
      };

      this.loadEntityStats();
    },

    async loadEntityStats() {
      if (!this.selectedEntity || !this.selectedProjectId) return;

      this.entityStatsLoading = true;
      try {
        const params = {
          project_id: this.selectedProjectId,
          entity_id: this.selectedEntity.id,
          canonical_name: this.selectedEntity.canonical_name,
          entity_type: this.selectedEntity.entity_type,
          interval: this.entityTimeInterval
        };

        // Add aliases for merged entities
        if (this.selectedEntity.aliases && this.selectedEntity.aliases.length > 0) {
          params.aliases = this.selectedEntity.aliases.join(',');
        }

        if (this.publishedFrom) {
          params.published_from = this.publishedFrom + 'T00:00:00';
        }
        if (this.publishedTo) {
          params.published_to = this.publishedTo + 'T23:59:59';
        }

        const response = await dataClient.get('/dashboard/entity-stats', { params });
        this.entityStats = response.data;
      } catch (error) {
        console.error('Error loading entity stats:', error);
        this.entityStats = null;
      } finally {
        this.entityStatsLoading = false;
      }
    },

    closeEntityDetail() {
      this.selectedEntity = null;
      this.entityStats = null;
    },

    onEntityIntervalChange() {
      this.loadEntityStats();
    },

    onMentionsClick() {
      if (!this.selectedEntity || !this.selectedProjectId) return;

      // Open Messages page with entity_id filter
      const route = this.$router.resolve({
        name: 'monitoringMessages',
        query: {
          entity_id: this.selectedEntity.id,
          entity_name: this.selectedEntity.name,
          project_id: this.selectedProjectId
        }
      });
      window.open(route.href, '_blank');
    },

    onEntityTimelineClick(params) {
      if (!this.selectedEntity || !this.selectedProjectId || !params.dataIndex === undefined) return;

      const timeline = this.entityStats?.timeline;
      if (!timeline || !timeline[params.dataIndex]) return;

      const clickedDate = timeline[params.dataIndex].date;

      // Calculate date range based on interval
      let publishedFrom, publishedTo;
      const date = new Date(clickedDate);

      if (this.entityTimeInterval === 'day') {
        // Single day
        publishedFrom = clickedDate;
        publishedTo = clickedDate;
      } else if (this.entityTimeInterval === 'week') {
        // Week: find Monday and Sunday
        const dayOfWeek = date.getDay();
        const monday = new Date(date);
        monday.setDate(date.getDate() - (dayOfWeek === 0 ? 6 : dayOfWeek - 1));
        const sunday = new Date(monday);
        sunday.setDate(monday.getDate() + 6);
        publishedFrom = monday.toISOString().split('T')[0];
        publishedTo = sunday.toISOString().split('T')[0];
      } else if (this.entityTimeInterval === 'month') {
        // Month: first and last day
        const firstDay = new Date(date.getFullYear(), date.getMonth(), 1);
        const lastDay = new Date(date.getFullYear(), date.getMonth() + 1, 0);
        publishedFrom = firstDay.toISOString().split('T')[0];
        publishedTo = lastDay.toISOString().split('T')[0];
      }

      // Open Messages page with entity_id filter and date range
      const route = this.$router.resolve({
        name: 'monitoringMessages',
        query: {
          entity_id: this.selectedEntity.id,
          entity_name: this.selectedEntity.name,
          project_id: this.selectedProjectId,
          published_from: publishedFrom,
          published_to: publishedTo
        }
      });
      window.open(route.href, '_blank');
    }
  },
  async mounted() {
    await this.loadProjects();
    await this.loadStats();
  }
};
</script>

<style scoped>
.chart-card {
  height: 400px;
}

.chart-container {
  height: calc(100% - 48px);
  display: flex;
  align-items: center;
  justify-content: center;
}

.chart {
  width: 100%;
  height: 100%;
}

.no-data {
  color: #9e9e9e;
  font-size: 14px;
}

.cursor-pointer {
  cursor: pointer;
}

.cursor-move {
  cursor: move;
}

.table-column-transition {
  transition: all 0.3s ease;
}

.clickable-chart {
  cursor: pointer;
}

.clickable-card {
  cursor: pointer;
  transition: all 0.2s ease;
}

.clickable-card:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  transform: translateY(-2px);
}

.drag-ghost {
  opacity: 0.5;
  background: #c8ebfb;
}

.drag-handle {
  opacity: 0.5;
  transition: opacity 0.2s;
}

.drag-handle:hover {
  opacity: 1;
}

.widget-setting-item {
  border: 1px solid #e0e0e0;
  transition: background-color 0.2s;
}

.widget-setting-item:hover {
  background-color: #f5f5f5;
}

.settings-drag-handle {
  opacity: 0.5;
  transition: opacity 0.2s;
}

.settings-drag-handle:hover {
  opacity: 1;
}

.source-chip {
  cursor: pointer;
}

.hidden-source-chip {
  opacity: 0.5;
  border-style: dashed;
}

.hidden-source-chip:hover {
  opacity: 0.8;
}
</style>
